import type { UseQueryResult } from '@tanstack/react-query'
import type { ComponentProps, ReactNode } from 'react'

import { ACTIVITY_WINDOW_DAYS } from '@brickup/shared/constants'
import { STAFF_DASHBOARD } from '@brickup/shared/locales/de/staff-dashboard'
import { Badge, cn } from '@/components/base'
import { Panel, PanelSection } from '@/components/Internal/Panel'
import { QueryGate } from '@/components/Internal/QueryGate'

/**
 * A dashboard tab: rows of panels on the ground, one gap everywhere, and the
 * stand-ins for the tab's own query inside that body — so a failure and a
 * filled tab start at the same inset.
 */
export function DashboardData<T>({
  query,
  above,
  children,
}: {
  query: UseQueryResult<T>
  /** Rows that read their own endpoints. Outside the gate, so they fetch
   *  while the tab's query is still on its way. */
  above?: ReactNode
  children: (data: T) => ReactNode
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-ground">
      <div className="mx-auto flex max-w-screen-2xl flex-col gap-4 p-6">
        {above}
        <QueryGate
          query={query}
          loading="page"
          error={STAFF_DASHBOARD.loadFailed}
        >
          {children}
        </QueryGate>
      </div>
    </div>
  )
}

const COLUMNS = {
  /** A main list and a column of smaller panels beside it. */
  'wide-narrow': 'xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]',
  halves: 'xl:grid-cols-2',
  thirds: 'lg:grid-cols-2 xl:grid-cols-3',
} as const

/** A row of panels, stacked below the width they need side by side. */
export function DashboardRow({
  columns,
  children,
}: {
  columns: keyof typeof COLUMNS
  children: ReactNode
}) {
  return (
    <div className={cn('grid grid-cols-1 items-start gap-4', COLUMNS[columns])}>
      {children}
    </div>
  )
}

/** The badge saying which window a panel's figures cover. Beside the panel it
 *  sits in, so the two dashboards that show one cannot date it differently. */
export const WindowBadge = () => (
  <Badge size="sm" label={STAFF_DASHBOARD.lastDays(ACTIVITY_WINDOW_DAYS)} />
)

/**
 * A panel of a dashboard row: the dense size, one block of content, and a head
 * that holds the height of a control. Rows mix panels that carry a control
 * with panels that do not, and reserving the room in every head is what starts
 * their bodies on the same line.
 *
 * A panel whose frame holds two kinds of content — a section and a table —
 * builds on `Panel` itself.
 */
export function DashboardPanel({
  children,
  ...panel
}: Omit<ComponentProps<typeof Panel>, 'size' | 'reserveAction'>) {
  return (
    <Panel {...panel} size="sm" reserveAction>
      <PanelSection>{children}</PanelSection>
    </Panel>
  )
}
